import { TelegramClient } from "telegram";
import { StringSession } from "telegram/sessions";
import { apiId, apiHash, botUsername } from "./util.js";
import { setToken } from "./api.js";
import { COUNTRIES } from "./countries.js";
import { t, setLocale, type Locale } from "./i18n.js";
import { clearPreviews } from "./media-store.js";

const SESSION_KEY = "snail_session";

function randomVerifier(): string {
  const a = new Uint8Array(24);
  crypto.getRandomValues(a);
  return btoa(String.fromCharCode(...a)).replace(/[+/=]/g, "").slice(0, 32);
}

/** nonce = sha256(verifier), hex — matches the server's nonce_for_verifier. */
async function nonceFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier)
  );
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Prove identity through the bot and obtain a bearer token.
 * The browser holds a secret `verifier` and only ever tells the bot the derived
 * `nonce` (sha256). It sends /start web_<nonce> (also the auto-connect /start),
 * then polls /api/auth presenting the verifier so the server can bind
 * redemption to this browser. Marks the bot connected.
 */
async function exchangeViaBot(client: TelegramClient): Promise<void> {
  const verifier = randomVerifier();
  const nonce = await nonceFor(verifier);
  const botPeer = await client.getInputEntity(botUsername());
  await client.sendMessage(botPeer, { message: `/start web_${nonce}` });
  localStorage.setItem("snail_bot_started", "1");  // shared auto-connect flag

  for (let i = 0; i < 15; i++) {  // ~15s: bot DM round-trips in well under that
    const res = await fetch("/api/auth", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nonce, verifier }),
    });
    if (res.ok) {
      const body = await res.json();
      setToken(body.token);
      // Seed the localStorage override from the server's stored locale on
      // every login, unconditionally (no merge with an existing override —
      // a later in-session change via Settings is a later write and wins).
      if (body.locale) setLocale(body.locale as Locale);
      return;
    }
    await new Promise((r) => setTimeout(r, 1000));
  }
  throw new Error(t("auth.err_confirm"));
}

function newClient(session: string): TelegramClient {
  return new TelegramClient(
    new StringSession(session),
    apiId(),
    apiHash(),
    { connectionRetries: 3 }
  );
}

/** Outcome of trying to restore a saved session, so callers can distinguish a
 *  genuine logged-out state from a transient connection failure (which must NOT
 *  send a still-valid session to the login screen):
 *   - "none"      : no saved session → show login.
 *   - "deauth"    : saved session exists but Telegram rejected it (revoked) →
 *                   cleared; show login.
 *   - "client"    : connected + authorized → use it.
 *   - "unreachable": saved session, but we couldn't connect after retries →
 *                   keep the session, show a reconnect prompt, NOT login. */
export type SessionResult =
  | { status: "none" }
  | { status: "deauth" }
  | { status: "unreachable" }
  | { status: "client"; client: TelegramClient };

export async function restoreSession(): Promise<SessionResult> {
  const saved = localStorage.getItem(SESSION_KEY);
  if (!saved) return { status: "none" };
  // Retry the connect a few times — a reload often races a cold network and the
  // first connect throws even though the session is perfectly valid.
  let lastErr: unknown;
  for (let attempt = 0; attempt < 3; attempt++) {
    const client = newClient(saved);
    try {
      await client.connect();
      if (!(await client.isUserAuthorized())) {
        localStorage.removeItem(SESSION_KEY);
        return { status: "deauth" };
      }
      // Persist the (possibly mutated) session: GramJS rotates server salt / DC
      // state during connect, and without re-saving a second window can connect
      // with a staler string and get treated as a duplicate — which Telegram
      // resolves by revoking the auth key, forcing a fresh login. Saving here
      // keeps localStorage (shared across tabs/windows) the live source of truth.
      const refreshed = client.session.save() as unknown as string;
      if (refreshed && refreshed !== saved) {
        localStorage.setItem(SESSION_KEY, refreshed);
      }
      return { status: "client", client };
    } catch (e) {
      lastErr = e;
      try { await client.disconnect(); } catch { /* ignore */ }
      await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
    }
  }
  console.error("[restoreSession] could not connect (session kept):", lastErr);
  return { status: "unreachable" };
}

/**
 * Full interactive login. `getCode`/`getPassword` yield the user-entered code
 * and (only if 2FA) the password; client.start handles SESSION_PASSWORD_NEEDED.
 * On success: saves the session locally and proves identity through the bot.
 */
export async function login(
  phoneNumber: string,
  getCode: () => Promise<string>,
  getPassword: () => Promise<string>,
  onError: (e: Error) => void
): Promise<TelegramClient> {
  const client = newClient("");
  await client.connect();

  await client.start({
    phoneNumber: async () => phoneNumber,
    phoneCode: getCode,
    password: getPassword,
    onError: (err) => {
      onError(err as Error);
      return Promise.resolve(true); // stop on error
    },
  });

  localStorage.setItem(SESSION_KEY, client.session.save() as unknown as string);
  await exchangeViaBot(client);  // sends /start web_<nonce>, polls for token
  return client;
}

/** Re-mint a bearer token via the bot (e.g. token expired but session valid). */
export async function refreshToken(client: TelegramClient): Promise<void> {
  await exchangeViaBot(client);
}

export async function logout(): Promise<void> {
  // Awaited, not fire-and-forget: reload() starts navigating before an
  // unawaited IDB transaction even opens, so the previews would survive logout.
  await clearPreviews();
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem("snail_token");
  localStorage.removeItem("snail_bot_started");
  window.location.reload();
}

/** Assemble E.164 from picked dial code + local digits; null if implausible. */
function toE164(dial: string, local: string): string | null {
  const digits = local.replace(/\D/g, "");
  const full = `+${dial}${digits}`;
  return /^\+\d{7,15}$/.test(full) ? full : null;
}

export function renderAuthForm(root: HTMLElement, onDone: () => void): void {
  root.innerHTML = `
    <div class="form-group">
      <label>${t("auth.country")}</label>
      <select id="auth-country">
        ${COUNTRIES.map(c =>
          `<option value="${c.dial}"${c.iso2 === "US" ? " selected" : ""}>${c.name} (+${c.dial})</option>`).join("")}
      </select>
    </div>
    <div class="form-group">
      <label>${t("auth.phone_label")}</label>
      <input id="auth-phone" type="tel" inputmode="numeric" placeholder="11912345678">
    </div>
    <button class="btn" id="auth-send">${t("auth.send_code")}</button>
    <div id="auth-step2" style="display:none">
      <div class="form-group" style="margin-top:1rem">
        <label>${t("auth.code_label")}</label>
        <input id="auth-code" type="text" inputmode="numeric">
      </div>
      <div class="form-group" id="auth-pw-group" style="display:none">
        <label>${t("auth.password_label")}</label>
        <input id="auth-pw" type="password">
      </div>
      <button class="btn" id="auth-signin">${t("auth.sign_in")}</button>
    </div>
    <div id="auth-error" class="error-banner" style="display:none"></div>
  `;

  const showError = (m: string) => {
    const e = root.querySelector<HTMLElement>("#auth-error")!;
    e.style.display = "block";
    e.textContent = m;
  };

  let resolveCode: (c: string) => void;
  let resolvePw: (p: string) => void;
  const codePromise = () =>
    new Promise<string>((r) => (resolveCode = r));
  const pwPromise = () =>
    new Promise<string>((r) => {
      root.querySelector<HTMLElement>("#auth-pw-group")!.style.display = "block";
      resolvePw = r;
    });

  root.querySelector("#auth-send")!.addEventListener("click", async () => {
    const dial = root.querySelector<HTMLSelectElement>("#auth-country")!.value;
    const local = root.querySelector<HTMLInputElement>("#auth-phone")!.value;
    const phone = toE164(dial, local);
    if (!phone) {
      showError(t("auth.err_phone"));
      return;
    }
    root.querySelector<HTMLElement>("#auth-step2")!.style.display = "block";
    try {
      await login(
        phone,
        codePromise,
        pwPromise,
        (e) => showError(e.message)
      );
      onDone();
    } catch (e: any) {
      showError(e.message || t("auth.err_login"));
    }
  });

  root.querySelector("#auth-signin")!.addEventListener("click", () => {
    const code = root.querySelector<HTMLInputElement>("#auth-code")!.value.trim();
    if (resolveCode!) resolveCode(code);
    const pw = root.querySelector<HTMLInputElement>("#auth-pw")?.value;
    if (pw && resolvePw!) resolvePw(pw);
  });
}
